import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Clock, TrendingUp, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { FAQSchema } from '@/components/schema/FAQSchema';
import { BreadcrumbSchema } from '@/components/schema/BreadcrumbSchema';

export const VerkaufszeitpunktRadar = () => {
  const [formData, setFormData] = useState({
    assetklasse: '',
    plz: '',
    wault: '',
    objektzustand: '',
    mieter_bonitaet: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tool/verkaufszeitpunkt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          wault: parseFloat(formData.wault)
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        toast.success('Analyse abgeschlossen!');
      } else {
        toast.error('Fehler bei der Berechnung');
      }
    } catch (error) {
      toast.error('Netzwerkfehler. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const assetklassen = [
    { value: 'lebensmittelmarkt', label: 'Lebensmittelmarkt' },
    { value: 'discounter', label: 'Discounter / Drogerie' },
    { value: 'fachmarkt', label: 'Fachmarkt' },
    { value: 'fachmarktzentrum', label: 'Fachmarktzentrum' }
  ];

  const zustaende = [
    { value: 'neuwertig', label: 'Neuwertig' },
    { value: 'gut', label: 'Gut' },
    { value: 'durchschnittlich', label: 'Durchschnittlich' },
    { value: 'renovierungsbedürftig', label: 'Renovierungsbedürftig' }
  ];

  const bonitaeten = [
    { value: 'exzellent', label: 'Exzellent (z.B. EDEKA, REWE)' },
    { value: 'gut', label: 'Gut' },
    { value: 'durchschnittlich', label: 'Durchschnittlich' },
    { value: 'schwach', label: 'Schwach' }
  ];

  const faqData = [
    {
      question: "Wie genau ist die Verkaufszeitpunkt-Einschätzung?",
      answer: "Die Einschätzung basiert auf aktuellen Marktdaten (Zinsniveau, Angebot/Nachfrage) und Ihren Objektdaten. Sie gibt eine fundierte Orientierung für die strategische Verkaufsplanung. Quartalsweise Updates gewährleisten Aktualität."
    },
    {
      question: "Was bedeuten die verschiedenen Empfehlungen?",
      answer: "JETZT VERKAUFEN (grün, Score 8-10): Optimale Marktbedingungen, höchste Verkaufspreise wahrscheinlich. GUTER ZEITPUNKT (gelb, Score 5-7): Solide Bedingungen, Verkauf empfohlen. BESSER WARTEN (rot, Score 0-4): Markt herausfordernd, evtl. 6-12 Monate warten oder Objekt optimieren."
    },
    {
      question: "Welche Faktoren beeinflussen die Empfehlung am stärksten?",
      answer: "Zinsniveau (30%) und Angebot/Nachfrage (25%) sind die wichtigsten Marktfaktoren. Dazu kommt der Asset-Class-Trend (20%) – Lebensmittelmärkte haben z.B. höhere Nachfrage als Fachmärkte. Ihre Objektqualität (10%) rundet die Bewertung ab."
    },
    {
      question: "Kann ich den Zeitpunkt selbst beeinflussen?",
      answer: "Ja! Durch Optimierung Ihres Objekts: WAULT verlängern (Mietverträge), Zustand verbessern (Sanierung), oder auf bessere Marktbedingungen warten. Unsere Tools zeigen Ihnen, wo Optimierungspotenzial besteht."
    },
    {
      question: "Wie oft wird die Markteinschätzung aktualisiert?",
      answer: "Wir aktualisieren die Marktfaktoren (Zinsen, Angebot/Nachfrage, Asset-Trends) quartalsweise. Für eine aktuelle Einschätzung nutzen Sie den Rechner einfach erneut."
    }
  ];

  const breadcrumbItems = [
    { name: "Home", url: `${window.location.origin}/` },
    { name: "Tools", url: `${window.location.origin}/tools` },
    { name: "Verkaufszeitpunkt-Radar", url: `${window.location.origin}/tools/verkaufszeitpunkt` }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <FAQSchema faqs={faqData} />
      <BreadcrumbSchema items={breadcrumbItems} />
      <SEO
        title="Verkaufszeitpunkt-Radar – Jetzt verkaufen oder warten? | RetailInvest"
        description="Ermitteln Sie in 2 Minuten, ob JETZT der richtige Zeitpunkt für den Verkauf Ihrer Handelsimmobilie ist. Basierend auf aktuellen Marktdaten und Objektqualität."
        url="/tools/verkaufszeitpunkt"
        keywords="Verkaufszeitpunkt Immobilie, Markt-Timing, Handelsimmobilie verkaufen wann, Verkaufsradar, optimaler Verkaufszeitpunkt"
      />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
              Verkaufszeitpunkt-Radar
            </h1>
            <p className="text-lg leading-relaxed max-w-3xl mx-auto" style={{ color: 'var(--gray-700)' }}>
              Ermitteln Sie in 2 Minuten, ob JETZT der richtige Zeitpunkt für den Verkauf ist.
              Basierend auf aktuellen Marktdaten und Ihrer Objektqualität.
            </p>
          </div>

          {/* Explanatory Content */}
          <Card className="glass-card p-8 mb-8 bg-[#EFF6FF]" data-testid="timing-info-card">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: 'var(--primary-700)' }}>
              Warum ist das Timing wichtig?
            </h2>
            <p className="text-base mb-4" style={{ color: 'var(--secondary-700)' }}>
              Der <strong>Verkaufszeitpunkt</strong> kann den Verkaufspreis um 10-20% beeinflussen. Bei ungünstigen 
              Marktbedingungen (hohe Zinsen, geringes Käuferinteresse) sinken die Preise. Bei günstigen Bedingungen 
              steigen sie. Unser Radar analysiert 5 Faktoren und gibt Ihnen eine klare Empfehlung.
            </p>
            <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: 'var(--primary-700)' }}>
              Die 5 Bewertungsfaktoren:
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: 'var(--gold-600)' }}></div>
                <p style={{ color: 'var(--secondary-700)' }}>
                  <strong>Zinsniveau (30%):</strong> Niedrige Zinsen = höhere Preise
                </p>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: 'var(--gold-600)' }}></div>
                <p style={{ color: 'var(--secondary-700)' }}>
                  <strong>Angebot & Nachfrage (25%):</strong> Viele Käufer, wenig Objekte = höhere Preise
                </p>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: 'var(--gold-600)' }}></div>
                <p style={{ color: 'var(--secondary-700)' }}>
                  <strong>Asset-Class-Trend (20%):</strong> Lebensmittelmärkte haben höchste Nachfrage
                </p>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: 'var(--gold-600)' }}></div>
                <p style={{ color: 'var(--secondary-700)' }}>
                  <strong>Saisonalität (15%):</strong> Q1/Q2 sind die besten Verkaufsquartale
                </p>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: 'var(--gold-600)' }}></div>
                <p style={{ color: 'var(--secondary-700)' }}>
                  <strong>Ihre Objektqualität (10%):</strong> WAULT, Zustand, Mieterbonität
                </p>
              </li>
            </ul>
          </Card>

          {/* Form */}
          <Card className="glass-card p-8 mb-8" data-testid="timing-form-card">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="assetklasse">Assetklasse <span className="text-red-500">*</span></Label>
                  <Select value={formData.assetklasse} onValueChange={(value) => setFormData({...formData, assetklasse: value})} required>
                    <SelectTrigger className="h-12" data-testid="select-assetklasse">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {assetklassen.map((ak) => (
                        <SelectItem key={ak.value} value={ak.value}>{ak.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="plz">PLZ-Gebiet <span className="text-red-500">*</span></Label>
                  <Input
                    id="plz"
                    required
                    placeholder="z.B. 80, 50, 60-69"
                    value={formData.plz}
                    onChange={(e) => setFormData({ ...formData, plz: e.target.value })}
                    className="h-12"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="wault">WAULT (Jahre) <span className="text-red-500">*</span></Label>
                  <Input
                    id="wault"
                    type="number"
                    step="0.1"
                    required
                    placeholder="z.B. 8.5"
                    value={formData.wault}
                    onChange={(e) => setFormData({ ...formData, wault: e.target.value })}
                    className="h-12"
                  />
                  <p className="text-sm mt-1" style={{ color: 'var(--gray-500)' }}>
                    Nutzen Sie unseren WAULT-Rechner, falls unbekannt
                  </p>
                </div>

                <div>
                  <Label htmlFor="objektzustand">Objektzustand <span className="text-red-500">*</span></Label>
                  <Select value={formData.objektzustand} onValueChange={(value) => setFormData({...formData, objektzustand: value})} required>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {zustaende.map((z) => (
                        <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="mieter_bonitaet">Mieterbonität <span className="text-red-500">*</span></Label>
                <Select value={formData.mieter_bonitaet} onValueChange={(value) => setFormData({...formData, mieter_bonitaet: value})} required>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {bonitaeten.map((b) => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#2563A0] text-white hover:bg-[#1B4D7A] font-semibold text-base"
                data-testid="calculate-timing-button"
              >
                <Clock className="h-5 w-5 mr-2" />
                {loading ? 'Analysiere...' : 'Verkaufszeitpunkt analysieren'}
              </Button>
            </form>
          </Card>

          {/* Result */}
          {result && (
            <Card className="glass-card p-8 mb-8" data-testid="timing-result-card">
              {/* Ampel & Score */}
              <div className="text-center mb-8">
                <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full mb-6 ${
                  result.ampel === 'gruen' ? 'bg-green-100' :
                  result.ampel === 'gelb' ? 'bg-yellow-100' :
                  'bg-red-100'
                }`}>
                  <div className={`text-5xl font-bold ${
                    result.ampel === 'gruen' ? 'text-green-600' :
                    result.ampel === 'gelb' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {result.score.toFixed(1)}
                  </div>
                </div>
                
                <h2 className={`text-3xl font-bold mb-4 ${
                  result.ampel === 'gruen' ? 'text-green-700' :
                  result.ampel === 'gelb' ? 'text-yellow-700' :
                  'text-red-700'
                }`}>
                  {result.empfehlung}
                </h2>
                
                <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--gray-700)' }}>
                  {result.begruendung}
                </p>
              </div>

              {/* Faktoren-Analyse */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                  Detaillierte Faktor-Analyse
                </h3>
                {result.faktoren.map((faktor, idx) => (
                  <div key={idx} className="p-4 bg-white rounded-lg border" style={{ borderColor: 'var(--gray-200)' }}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-bold text-base" style={{ color: 'var(--navy-700)' }}>
                          {faktor.name}
                        </h4>
                        <p className="text-sm" style={{ color: 'var(--gray-600)' }}>
                          {faktor.beschreibung}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold" style={{ color: 'var(--navy-900)' }}>
                          {faktor.score}/10
                        </div>
                        <div className="text-xs" style={{ color: 'var(--gray-500)' }}>
                          Gewichtung: {faktor.gewichtung}
                        </div>
                      </div>
                    </div>
                    
                    {/* Score Bar */}
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full transition-all duration-500"
                        style={{ 
                          width: `${(faktor.score / 10) * 100}%`,
                          backgroundColor: 
                            faktor.score >= 7 ? 'var(--success-500)' :
                            faktor.score >= 5 ? 'var(--warning-500)' :
                            'var(--error-500)'
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                <p className="text-sm" style={{ color: 'var(--gray-700)' }}>
                  <Info className="inline h-4 w-4 mr-2" style={{ color: 'var(--navy-600)' }} />
                  <strong>Nächste Markt-Aktualisierung:</strong> {result.naechste_pruefung}
                </p>
              </div>
            </Card>
          )}

          {/* FAQ */}
          <Card className="glass-card p-8" data-testid="timing-faq-card">
            <h2 className="text-2xl font-semibold mb-6" style={{ color: 'var(--primary-700)' }}>
              Häufig gestellte Fragen (FAQ)
            </h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`} className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left font-semibold" style={{ color: 'var(--primary-700)' }}>
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base" style={{ color: 'var(--secondary-700)' }}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VerkaufszeitpunktRadar;
